import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { DefaultPhoneNumber } from 'src/app/components/Elements/Location';
import { getStarted, home, howItWorks, loggedInTransferRx } from 'src/app/router/paths';
import { HOME_ELEMENT_IDS } from 'src/consts';

const questions = (loggedIn: boolean, dispatchSegmentTrack: (event: UnifiedEvent, payload: object) => void) => {
  const getLinkProps = (source: string) => {
    return loggedIn
      ? { to: loggedInTransferRx.toPath() }
      : {
          to: getStarted.toPath(),
          onClick: () =>
            dispatchSegmentTrack(UNIFIED.beginOnboarding, { pagepath: window?.location?.pathname, source }),
        };
  };
  return [
    {
      question: <>How do I send a new prescription to Capsule?</>,
      answer: (
        <p>
          Every doctor can send prescriptions to Capsule. Ask your doctor to e-prescribe to: <b>Capsule</b>. They can
          always call <DefaultPhoneNumber className="green" />. Once it&#8217;s ready, we&#8217;ll send you a text to
          setup your payment and schedule your free delivery. You won&#8217;t have to lift a finger.
          <br />
          <br />
          <LinkWithQuery to={howItWorks.toPath()} className="green">
            Learn more about sending a new prescription to Capsule.
          </LinkWithQuery>
        </p>
      ),
    },
    {
      question: <>How do I transfer refills from my old pharmacy to Capsule?</>,
      answer: (
        <p>
          Just{' '}
          <LinkWithQuery {...getLinkProps('Provide Us')} className="green">
            provide us
          </LinkWithQuery>{' '}
          with some information about your medications and we&#8217;ll take care of the rest. We contact your old
          pharmacy, get your insurance information, and transfer your refills to our pharmacy. When it&#8217;s time for
          your next refill, we&#8217;ll text you to setup your payment and schedule your free delivery. You won&#8217;t
          have to do a thing!
          <br />
          <br />
          <LinkWithQuery {...getLinkProps('Get Started Now')} className="green">
            Get started now.
          </LinkWithQuery>
        </p>
      ),
    },
    {
      question: <div data-cy={'faq-how-does-capsule-work'}>How does Capsule work?</div>,
      answer: (
        <p data-cy={'faq-how-does-capsule-work-answer'}>
          Capsule gets your new prescriptions{' '}
          <LinkWithQuery to={howItWorks.toPath()} className="green">
            from your doctor
          </LinkWithQuery>{' '}
          or your refills from your{' '}
          <LinkWithQuery {...getLinkProps('Old Pharmacy')} className="green">
            old pharmacy
          </LinkWithQuery>
          . We fill your prescriptions in our pharmacy and then text you to schedule your free delivery. You never worry
          about your meds again. We&#8217;ll coordinate refills with your doctor and copays with your insurance company
          forever and always.
        </p>
      ),
    },
    {
      question: <>How much does Capsule cost? Is delivery really free?</>,
      answer: (
        <p>
          Yes! Our delivery is free so you only pay your copay. Capsule accepts all major insurance and your copay will
          not change. We will always let you know how much your prescription costs before you schedule your delivery.
        </p>
      ),
    },
    {
      question: <div data-cy={'faq-when-and-where-do-you-deliver'}>When and where do you deliver?</div>,
      answer: (
        <p data-cy={'faq-when-and-where-do-you-deliver-answer'}>
          Capsule hand delivers your medication within several cities across the US.{' '}
          {loggedIn ? (
            ''
          ) : (
            <>
              You can check your zip code{' '}
              <LinkWithQuery to={`${home.toPath()}#${HOME_ELEMENT_IDS.deliveryArea}`}>here</LinkWithQuery>.{' '}
            </>
          )}
          There just needs to be someone – a friend, a doorman, a colleague – to receive your medication when it
          arrives.
        </p>
      ),
    },
    {
      question: <>Who will deliver my medication?</>,
      answer: (
        <p>
          All of our couriers undergo rigorous background checks and training, including the DOT Commercial Bicyclist
          Safety Course and HIPAA security training. To ensure your privacy, your medication will arrive in a discreet,
          tamper sealed package and only you and your pharmacist will know what&#8217;s inside.
        </p>
      ),
    },
    {
      question: <>Can I order for a family member?</>,
      answer: (
        <p>
          Yes. It&#8217;s easy to manage your family&#8217;s medication with Capsule. Ask your doctor to{' '}
          <LinkWithQuery to={howItWorks.toPath()} className="green">
            send new prescriptions
          </LinkWithQuery>{' '}
          directly to Capsule or{' '}
          <LinkWithQuery {...getLinkProps("Transfer Your Family's Refills")} className="green">
            transfer your family&#8217;s refills
          </LinkWithQuery>{' '}
          to Capsule. We&#8217;ll text you to setup your payment and schedule your free delivery.
        </p>
      ),
    },
    {
      question: <>How do I refill my medication?</>,
      answer: (
        <p>
          Capsule offers smart refills so you never have to worry about your medication. We coordinate with your doctor
          and insurance company to make sure you never run out of the medication you need or pay too much for a
          prescription. We will text you to schedule delivery when your refills are ready.
        </p>
      ),
    },
    {
      question: <>Is my information secure?</>,
      answer: (
        <p>
          Your information is always safe with us. We store all of your personal data in an encrypted HIPAA-compliant
          environment.
        </p>
      ),
    },
    {
      question: <>What if my prescription requires a prior authorization?</>,
      answer: (
        <p>
          We work with your doctor and insurance company to quickly and efficiently handle your prior authorization.
          We&#8217;ll keep you updated so you know exactly how long the process will take.
        </p>
      ),
    },
    {
      question: <>Do you deliver controlled substances?</>,
      answer: <p>Yes. For controlled substances, we will need to verify your identification before we deliver.</p>,
    },
    {
      question: <>How can I get an FSA receipt?</>,
      answer: (
        <p>
          To get a copy of your FSA receipt, sign in to Capsule via the iOS app or website and click &ldquo;Your
          Orders&rdquo; from the navigation menu. Open the order for which you need a receipt and click &ldquo;View
          Receipt,&rdquo; where you will be able to export your FSA receipt as a PDF.
        </p>
      ),
    },
    {
      question: <>What if I have a special request?</>,
      answer: (
        <p>
          Feel free to ask your pharmacist. We offer medication syncing, medication counseling, delivery of temperature
          controlled medications, appointment reminders and more. We work with you to manage your medication.
        </p>
      ),
    },
  ];
};

export default questions;
